import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import {
  SupplierFullDtoIn,
  SupplierTaxApprovalStatus,
  isSupplierTaxApprovalActionable,
} from '@/models/in/SupplierFullDtoIn';
import { SupportRequestStatuses } from '@/models/common/SupportRequestStatuses';
import { SupportRequestInitialStatusEnum } from '@/models/common/SupportRequestInitialStatusEnum';

/**
 * Status transitions that the secretariat treats as "approving" the request -
 * the moment a request moves to one of these statuses, we want the secretariat
 * to see the tax-withholding gate (confirm missing/expired approvals + show
 * required withholdings) so they don't pay a supplier without checking.
 *
 * There are two distinct status pipelines:
 *   - `SupportRequestStatuses` - the main lifecycle (PendingInitialApproval →
 *     InitialApprovalApproved → PendingExecution → …). Used after the initial-
 *     status process has completed.
 *   - `SupportRequestInitialStatusEnum` - the initial-status pipeline used
 *     while `isInInitialStatusProcess=true` (PendingFirstReview → Approved /
 *     Rejected / …). The "Approved" terminal state here is what flips the
 *     real status to InitialApprovalApproved on the backend.
 *
 * We have to recognize the "approving" status name from BOTH enums because
 * the secretariat reaches "approve" from either pipeline depending on where
 * the request is in its lifecycle.
 */
const APPROVAL_STATUSES: ReadonlySet<string> = new Set([
  SupportRequestStatuses[SupportRequestStatuses.InitialApprovalApproved],
  SupportRequestStatuses[SupportRequestStatuses.PendingExecution],
  SupportRequestInitialStatusEnum[SupportRequestInitialStatusEnum.Approved],
]);

export function isApprovalStatus(newStatusName: string | undefined | null): boolean {
  if (!newStatusName) return false;
  return APPROVAL_STATUSES.has(newStatusName);
}

/**
 * One row in the "missing approval" confirmation dialog. Shown when a
 * selected supplier has withholding configured but the approval is in an
 * actionable state (PendingValidUntil / PendingDocument / Expired).
 */
export interface SupplierApprovalIssue {
  supplierId: number;
  supplierName: string;
  status: SupplierTaxApprovalStatus;
  /** ISO date of the (expired) approval, when applicable. */
  taxApprovalValidUntil?: string | null;
}

/**
 * One row in the "withholdings" notice dialog: only the supplier name and
 * the configured percentage (no amounts, no request counts - that level of
 * detail belongs in the per-request workflow, not in the global heads-up).
 */
export interface SupplierWithholdingRow {
  supplierId: number;
  supplierName: string;
  percentage: number;
}

interface GateData {
  approvalIssues: SupplierApprovalIssue[];
  withholdings: SupplierWithholdingRow[];
}

/**
 * Walk the selected support requests, dedupe by supplier and return the data
 * the SupplierApprovalGate needs for its two dialogs:
 *
 *   - approvalIssues: suppliers whose tax-approval status requires confirmation
 *     before approving payment for them (PendingValidUntil / PendingDocument /
 *     Expired). Empty when no supplier is in an actionable state.
 *
 *   - withholdings: suppliers with an active withholding percentage (> 0)
 *     across the selected requests. Used to remind the secretariat to deduct
 *     manually before paying. Includes suppliers with actionable approvals
 *     too - they still need to be withheld, the approval-issues dialog is a
 *     separate compliance reminder, not a filter.
 *
 * Both lists deduplicate by supplier id.
 */
export function computeSupplierApprovalGate(
  selectedRequests: ManagementSupportRequestDtoIn[],
): GateData {
  const approvalIssuesById = new Map<number, SupplierApprovalIssue>();
  const withholdingsById = new Map<number, SupplierWithholdingRow>();

  for (const req of selectedRequests) {
    const supplier: SupplierFullDtoIn | null | undefined = req.supplier;
    if (!supplier) continue;

    if (isSupplierTaxApprovalActionable(supplier.taxApprovalStatus)
        && !approvalIssuesById.has(supplier.id)) {
      approvalIssuesById.set(supplier.id, {
        supplierId: supplier.id,
        supplierName: supplier.name,
        status: supplier.taxApprovalStatus,
        taxApprovalValidUntil: supplier.taxApprovalValidUntil ?? null,
      });
    }

    const pct = supplier.taxWithholdingPercentage ?? 0;
    if (pct > 0 && !withholdingsById.has(supplier.id)) {
      withholdingsById.set(supplier.id, {
        supplierId: supplier.id,
        supplierName: supplier.name,
        percentage: pct,
      });
    }
  }

  return {
    approvalIssues: Array.from(approvalIssuesById.values()).sort((a, b) =>
      (a.supplierName ?? '').localeCompare(b.supplierName ?? '', 'he'),
    ),
    withholdings: Array.from(withholdingsById.values()).sort((a, b) =>
      (a.supplierName ?? '').localeCompare(b.supplierName ?? '', 'he'),
    ),
  };
}
